import { useEffect, useState } from 'react'
import { listPaymentTypes, deletePaymentType, type PaymentType } from '@/api/payment-types'
import { PaymentTypeDialog } from './PaymentTypeDialog'
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { Plus, Package, Pencil, Trash2, Tag } from 'lucide-react'

function formatCOP(n: number) {
  return `$${Number(n).toLocaleString('es-CO')}`
}

export function PaymentTypeList() {
  const [types, setTypes] = useState<PaymentType[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<PaymentType | null>(null)
  const [pendingDelete, setPendingDelete] = useState<PaymentType | null>(null)

  useEffect(() => {
    listPaymentTypes()
      .then(r => setTypes(r.payment_types))
      .catch(() => toast.error('Error al cargar productos'))
      .finally(() => setLoading(false))
  }, [])

  function openCreate() { setEditing(null); setDialogOpen(true) }
  function openEdit(t: PaymentType) { setEditing(t); setDialogOpen(true) }

  function handleSaved(saved: PaymentType) {
    setTypes(prev => {
      const idx = prev.findIndex(t => t.id === saved.id)
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next }
      return [...prev, saved]
    })
    setDialogOpen(false)
  }

  async function handleDelete(t: PaymentType) {
    try {
      await deletePaymentType(t.id)
      setTypes(prev => prev.filter(x => x.id !== t.id))
      toast.success('Producto eliminado')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al eliminar')
    }
  }

  return (
    <div className="flex flex-col gap-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-text-primary font-bold text-base leading-tight">Productos y servicios</h2>
          <p className="text-text-muted text-xs mt-1">
            Cobros rápidos: masajes, tiqueteras, ingresos diarios, etc.
            {!loading && types.length > 0 && (
              <span className="ml-2 font-semibold text-text-primary">{types.length} configurados</span>
            )}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-accent text-background text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-accent-hover transition-colors cursor-pointer shadow-lg shadow-accent/20 shrink-0"
        >
          <Plus className="w-4 h-4" />
          Nuevo
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-surface border border-border rounded-2xl p-4 animate-pulse h-28" />
          ))}
        </div>
      ) : types.length === 0 ? (
        <div className="bg-surface border border-border rounded-2xl p-10 flex flex-col items-center gap-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center">
            <Package className="w-7 h-7 text-accent" />
          </div>
          <div>
            <p className="text-text-primary font-semibold text-sm">Sin productos configurados</p>
            <p className="text-text-muted text-xs mt-1 max-w-xs">
              Agrega tus servicios y productos para cobrarlos rápidamente desde el registro de pagos.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-accent text-background text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-accent-hover transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            Crear primer producto
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {types.map(t => (
            <ProductCard
              key={t.id}
              type={t}
              onEdit={() => openEdit(t)}
              onDelete={() => setPendingDelete(t)}
            />
          ))}
        </div>
      )}

      <PaymentTypeDialog open={dialogOpen} type={editing} onClose={() => setDialogOpen(false)} onSaved={handleSaved} />

      <AlertDialog open={!!pendingDelete} onOpenChange={() => setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogTitle>¿Eliminar "{pendingDelete?.name}"?</AlertDialogTitle>
          <AlertDialogDescription>
            Ya no aparecerá como opción en el formulario de cobros. Esta acción no se puede deshacer.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { handleDelete(pendingDelete!); setPendingDelete(null) }}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function ProductCard({ type, onEdit, onDelete }: {
  type: PaymentType
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-3 group hover:border-accent/30 hover:shadow-lg hover:shadow-black/10 hover:-translate-y-0.5 transition-all duration-200 relative">

      {/* Icon + actions row */}
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-xl bg-accent/10 border border-accent/15 flex items-center justify-center shrink-0">
          <Tag className="w-4 h-4 text-accent" />
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={onEdit}
            aria-label="Editar"
            className="p-1.5 rounded-lg text-text-muted hover:text-accent hover:bg-accent/10 transition-colors cursor-pointer"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            aria-label="Eliminar"
            className="p-1.5 rounded-lg text-text-muted hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Name + description */}
      <div className="flex-1 min-w-0">
        <p className="text-text-primary text-sm font-bold leading-snug truncate">{type.name}</p>
        {type.description && (
          <p className="text-text-muted text-[11px] mt-0.5 line-clamp-2 leading-snug">{type.description}</p>
        )}
      </div>

      {/* Price */}
      <div className="border-t border-border pt-2.5">
        <p className="text-accent font-black text-base leading-tight">{formatCOP(Number(type.price))}</p>
        <p className="text-text-muted text-[10px]">COP</p>
      </div>
    </div>
  )
}
